/**
 * Errores del navegador al pixel propio (2-10-2026), para que lleguen al bot de avisos (Lambda `app_events`).
 *
 *   js_error   excepciones no capturadas y promesas rechazadas (msg y dónde, saneados)
 *   api_error  fallos de la API que el usuario no ve como tal: 5xx, red caída, 408, 413, 429
 *              (los 401/402/403/404/422 son flujo normal; la subida ya manda `upload_error`)
 *
 * Contrato con `app_events`: props escalares `status`, `method`, `where`, `msg` (≤ 120 caracteres).
 * `js_error` lleva además `name` (tipo de lo lanzado) y `stack` (hasta 3 marcos `fichero:línea:columna`): el bundle es
 * una sola línea, así que sin columna no se puede situar el fallo (los 7 de Colombia del 8-10 decían solo «Uncaught
 * main.js:2»). Con el `.map` del build (`app/build/static/js/`) se traduce a fuente.
 * Nada de correos, tokens ni valores de documentos: se recortan URLs y direcciones. Máx. 5 `js_error` por carga de página.
 */
import { track, normalizePath } from "./track";

const clean = (s) =>
  String(s || "")
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "<email>")
    .replace(/(https?:\/\/[^\s?#"']+)[^\s"']*/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);

const IGNORE = /ResizeObserver loop|^Script error\.?$|Non-Error promise rejection captured/i;
let jsErrors = 0;

// «Error», «TypeError»… o el tipo de lo lanzado si no es un Error (`throw "x"`, `throw {}` dan «Uncaught …» sin más).
const errName = (err) => {
  if (err instanceof Error) return err.name || "Error";
  if (err === null) return "null";
  return typeof err === "object" ? `object:${err?.constructor?.name || "?"}` : typeof err;
};

// Los primeros marcos de la pila como `fichero:línea:columna`, sin dominio ni consulta.
export function shortStack(err) {
  const raw = String(err?.stack || "");
  const frames = [];
  for (const m of raw.matchAll(/([\w.-]+\.js)(?:\?[^:\s)]*)?:(\d+):(\d+)/g)) {
    frames.push(`${m[1]}:${m[2]}:${m[3]}`);
    if (frames.length >= 3) break;
  }
  return frames.join(" < ").slice(0, 120);
}

export function reportApiError(err) {
  try {
    const status = err?.response?.status || 0;
    const url = String(err?.config?.url || "");
    if (!(status === 0 || status >= 500 || status === 408 || status === 413 || status === 429)) return;
    if (/\/events$/.test(url) || /\/scanners\/[^/]+\/process$/.test(url)) return; // evita bucles; la subida tiene su propio evento
    track("api_error", {
      status,
      method: String(err?.config?.method || "get").toUpperCase(),
      where: normalizePath(url.replace(/^https?:\/\/[^/]+/, "")),
      msg: clean(err?.response?.data?.message || err?.message),
    });
  } catch {
    // una métrica nunca rompe la app
  }
}

export function installErrorEvents() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    try {
      if (jsErrors >= 5 || IGNORE.test(e.message || "")) return;
      jsErrors += 1;
      const file = String(e.filename || "").split("/").pop().split("?")[0];
      track("js_error", {
        msg: clean(e.message),
        where: file ? `${file}:${e.lineno || 0}:${e.colno || 0}` : "",
        name: errName(e.error),
        stack: shortStack(e.error),
      });
    } catch {
      // idem
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    try {
      const msg = e?.reason?.message || String(e?.reason || "");
      if (jsErrors >= 5 || IGNORE.test(msg)) return;
      jsErrors += 1;
      track("js_error", { msg: clean(msg), where: "promise", name: errName(e?.reason), stack: shortStack(e?.reason) });
    } catch {
      // idem
    }
  });
}
